import { useState, useEffect, useMemo } from 'react';
import { History, Loader2, Inbox } from 'lucide-react';
import type { Tutee } from '../../../types/tuition';
import { fetchStudentsForTutee } from '../../../services/tuteeService';
import {
  fetchFlashcardReviewLog,
  resolveStudentSlug,
  type FlashcardReviewLogEntry,
} from '../../../services/flashcardService';
import type { Rating } from '../../../lib/spacedRepetition/sm2';
import {
  flashcardSgDayKey,
  formatFlashcardDayHeader,
  formatFlashcardLogTime,
} from '../../../utils/flashcardInsights';

const RATING_STYLES: Record<Rating, { label: string; className: string }> = {
  again: { label: 'Again', className: 'bg-red-100 text-red-800' },
  hard: { label: 'Hard', className: 'bg-orange-100 text-orange-800' },
  good: { label: 'Good', className: 'bg-green-100 text-green-800' },
  easy: { label: 'Easy', className: 'bg-blue-100 text-blue-800' },
};

interface FlashcardStudyHistoryProps {
  tuteeId: string;
  selectedTutee?: Tutee;
  /** When set, only show reviews for cards in the current deck filter. */
  deckCardIds: string[];
  deckLabel: string;
}

const FlashcardStudyHistory = ({
  tuteeId,
  selectedTutee,
  deckCardIds,
  deckLabel,
}: FlashcardStudyHistoryProps) => {
  const [students, setStudents] = useState<{ value: string; label: string }[]>([]);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [entries, setEntries] = useState<FlashcardReviewLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!tuteeId) return;
    fetchStudentsForTutee(tuteeId)
      .then((list) => {
        const opts = list.map((s) => ({
          value: s.studentName.toLowerCase(),
          label: s.studentName.charAt(0).toUpperCase() + s.studentName.slice(1),
        }));
        setStudents(opts);
        if (opts.length === 1) {
          setSelectedStudent(opts[0].value);
        } else if (opts.length === 0) {
          setSelectedStudent(tuteeId);
        } else {
          setSelectedStudent((prev) =>
            prev && opts.some((o) => o.value === prev) ? prev : opts[0]?.value ?? tuteeId
          );
        }
      })
      .catch(() => {
        setStudents([]);
        setSelectedStudent(tuteeId);
      });
  }, [tuteeId]);

  const studentSlug = useMemo(
    () =>
      resolveStudentSlug(
        tuteeId,
        students.map((s) => ({ studentName: s.value })),
        selectedStudent
      ),
    [tuteeId, students, selectedStudent]
  );

  useEffect(() => {
    if (!tuteeId || !studentSlug) return;
    let cancelled = false;
    setLoading(true);
    setError('');
    fetchFlashcardReviewLog(tuteeId, studentSlug, {
      cardIds: deckCardIds,
    })
      .then((rows) => {
        if (!cancelled) setEntries(rows);
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) setError('Could not load study history.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tuteeId, studentSlug, deckCardIds.join(',')]);

  const groupedByDay = useMemo(() => {
    const groups: { dayKey: string; header: string; items: FlashcardReviewLogEntry[] }[] = [];
    const map = new Map<string, FlashcardReviewLogEntry[]>();
    for (const entry of entries) {
      const key = flashcardSgDayKey(entry.reviewedAt);
      const list = map.get(key) ?? [];
      list.push(entry);
      map.set(key, list);
    }
    for (const [dayKey, items] of map) {
      groups.push({
        dayKey,
        header: formatFlashcardDayHeader(items[0].reviewedAt),
        items,
      });
    }
    return groups;
  }, [entries]);

  const studentLabel =
    students.find((s) => s.value === selectedStudent)?.label ??
    (students.length === 0 ? 'Group' : selectedStudent);

  return (
    <section className="mt-2 p-4 sm:p-5 bg-gradient-to-br from-slate-50 to-indigo-50/40 rounded-2xl border border-indigo-100/80">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div>
          <h3 className="text-sm font-black text-gray-900 flex items-center gap-2">
            <History className="w-4 h-4 text-indigo-600" />
            Study history
          </h3>
          <p className="text-xs text-gray-600 mt-1">
            Reviews in <span className="font-semibold">{deckLabel}</span>
            {entries.length > 0 && ` · ${entries.length} recent`}
          </p>
        </div>
        {students.length > 1 && (
          <div className="flex flex-wrap gap-1.5">
            {students.map((s) => {
              const active = selectedStudent === s.value;
              return (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => setSelectedStudent(s.value)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${
                    active
                      ? selectedTutee
                        ? `bg-gradient-to-r ${selectedTutee.colorScheme.gradient} text-white shadow-sm`
                        : 'bg-indigo-600 text-white'
                      : 'bg-white text-gray-600 border border-gray-200 hover:border-gray-300'
                  }`}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        )}
        {students.length === 1 && (
          <span className="text-xs font-bold text-gray-500 bg-white/80 px-2.5 py-1 rounded-full border border-gray-100">
            {studentLabel}
          </span>
        )}
      </div>

      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
        </div>
      ) : entries.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-center text-gray-500">
          <Inbox className="w-10 h-10 text-gray-300 mb-2" />
          <p className="text-sm font-semibold">No reviews yet</p>
          <p className="text-xs mt-1 max-w-xs">
            {studentLabel} hasn&apos;t studied cards in this deck yet, or history is empty for this
            filter.
          </p>
        </div>
      ) : (
        <div className="max-h-[22rem] overflow-y-auto overscroll-contain space-y-4 pr-0.5">
          {groupedByDay.map((group) => (
            <div key={group.dayKey}>
              <div className="sticky top-0 z-10 py-1.5 mb-1.5">
                <span className="inline-block text-[10px] font-black uppercase tracking-wider text-indigo-800 bg-white/90 backdrop-blur px-2.5 py-1 rounded-lg border border-indigo-100 shadow-sm">
                  {group.header}
                </span>
              </div>
              <ul className="space-y-1.5">
                {group.items.map((entry) => {
                  const rating = RATING_STYLES[entry.rating];
                  return (
                    <li
                      key={entry.id}
                      className="flex items-start gap-3 p-2.5 sm:p-3 bg-white rounded-xl border border-gray-100 shadow-sm text-sm"
                    >
                      <time
                        className="text-[11px] font-bold text-gray-400 tabular-nums w-14 flex-shrink-0 pt-0.5"
                        dateTime={entry.reviewedAt}
                      >
                        {formatFlashcardLogTime(entry.reviewedAt)}
                      </time>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 truncate" title={entry.cardFront}>
                          {entry.cardFront}
                        </p>
                        <p className="text-xs text-gray-500 truncate mt-0.5" title={entry.cardBack}>
                          {entry.cardBack}
                        </p>
                      </div>
                      <span
                        className={`flex-shrink-0 px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wide ${rating.className}`}
                      >
                        {rating.label}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default FlashcardStudyHistory;
